import { SaveManager, type WorldState } from '../../../state/SaveManager.js';
import {
  isCharacterGroveSnapshot,
  type CharacterGroveSnapshot,
} from './minigames/character-grove/CharacterGroveState.js';

export const STORY_FOREST_WORLD_ID = 'story-forest';

export const STORY_FOREST_DISTRICTS = {
  silentStoryTree: 'silent-story-tree',
  wordGarden: 'word-garden',
  soundingStones: 'sounding-stones',
  fluencyFalls: 'fluency-falls',
  characterGrove: 'character-grove',
  informationalTrail: 'informational-trail',
  writersHollow: 'writers-hollow',
  libraryCanopy: 'library-canopy',
  masteryGrove: 'mastery-grove',
  sequenceStream: 'sequence-stream',
  mainIdeaMeadow: 'main-idea-meadow',
  inferenceHollow: 'inference-hollow',
  authorsCabin: 'authors-cabin',
  grammarGlade: 'grammar-glade',
  fluencyLanternPath: 'fluency-lantern-path',
  greatStoryTree: 'great-story-tree',
} as const;

export type StoryForestDistrict =
  typeof STORY_FOREST_DISTRICTS[keyof typeof STORY_FOREST_DISTRICTS];

export type StoryTreeStage = 'dormant' | 'awakened-1';
export type MemoryTreeStage = 'confused' | 'restored-1';

interface StoryForestWorldRecord extends WorldState {
  storyTreeStage?: StoryTreeStage;
  storyForestDistrictProgress?: Record<string, unknown>;
  completedStoryForestDistricts?: string[];
  storyForestRestorationEvents?: string[];
}

export interface StoryForestStateSnapshot {
  visited: boolean;
  currentDistrict: StoryForestDistrict;
  storyTreeDiscovered: boolean;
  storyTreeStage: StoryTreeStage;
  storyLanternsLit: readonly string[];
  completedStoryTrails: readonly string[];
  wordGardenDiscovered: boolean;
  completedWordGardenChallenges: readonly string[];
  wordGardenBlooms: readonly string[];
  characterGroveDiscovered: boolean;
  memoryTreeDiscovered: boolean;
  memoryTreeStage: MemoryTreeStage;
  characterGroveChallengeSnapshots: Readonly<Record<string, CharacterGroveSnapshot>>;
  completedCharacterGroveChallenges: readonly string[];
  restoredMemoryTrees: readonly string[];
  characterSkillMastery: Readonly<Record<string, readonly string[]>>;
  unlockedDistricts: readonly StoryForestDistrict[];
  districtProgress: Readonly<Record<string, unknown>>;
  completedDistricts: readonly StoryForestDistrict[];
  restorationEvents: readonly string[];
}

function getRecord() {
  return SaveManager.getWorld(STORY_FOREST_WORLD_ID) as StoryForestWorldRecord;
}

function addUnique(values: string[] | undefined, value: string) {
  const next = Array.isArray(values) ? values : [];
  if (!next.includes(value)) next.push(value);
  return next;
}

function asDistrict(value: string | undefined): StoryForestDistrict {
  return Object.values(STORY_FOREST_DISTRICTS).includes(value as StoryForestDistrict)
    ? value as StoryForestDistrict
    : STORY_FOREST_DISTRICTS.silentStoryTree;
}

function normalizeDistricts(values: string[] | undefined) {
  const known = new Set(Object.values(STORY_FOREST_DISTRICTS));
  return (Array.isArray(values) ? values : [])
    .filter((value): value is StoryForestDistrict => known.has(value as StoryForestDistrict));
}

function normalizeCharacterGroveSnapshots(values: Record<string, unknown> | undefined) {
  const snapshots: Record<string, CharacterGroveSnapshot> = {};
  Object.entries(values ?? {}).forEach(([challengeId, value]) => {
    if (isCharacterGroveSnapshot(value) && value.challengeId === challengeId) {
      snapshots[challengeId] = value;
    }
  });
  return snapshots;
}

function normalizeCharacterSkillMastery(values: Record<string, string[]> | undefined) {
  const mastery: Record<string, readonly string[]> = {};
  Object.entries(values ?? {}).forEach(([skillKind, challengeIds]) => {
    if (skillKind.trim() && Array.isArray(challengeIds)) {
      mastery[skillKind] = [
        ...new Set(challengeIds.filter(id => typeof id === 'string' && id.trim())),
      ];
    }
  });
  return mastery;
}

export function getStoryForestState(): StoryForestStateSnapshot {
  const world = getRecord();
  const unlockedDistricts = normalizeDistricts(world.unlockedDistricts);
  const storyTreeStage: StoryTreeStage = world.storyTreeStage === 'awakened-1'
    || world.completedStoryTrails?.includes('story-tree-sequence-1')
    ? 'awakened-1'
    : 'dormant';
  if (!unlockedDistricts.includes(STORY_FOREST_DISTRICTS.silentStoryTree)) {
    unlockedDistricts.unshift(STORY_FOREST_DISTRICTS.silentStoryTree);
  }
  // Backfill the new path for saves that completed Challenge 1 before Word
  // Garden existed. This is derived without resetting or rewriting the save.
  if (storyTreeStage === 'awakened-1'
      && !unlockedDistricts.includes(STORY_FOREST_DISTRICTS.wordGarden)) {
    unlockedDistricts.push(STORY_FOREST_DISTRICTS.wordGarden);
  }
  if (world.completedWordGardenChallenges?.includes('word-garden-context-1')
      && !unlockedDistricts.includes(STORY_FOREST_DISTRICTS.characterGrove)) {
    unlockedDistricts.push(STORY_FOREST_DISTRICTS.characterGrove);
  }
  if ((world.completedCharacterGroveChallenges?.length ?? 0) > 0
      && !unlockedDistricts.includes(STORY_FOREST_DISTRICTS.sequenceStream)) {
    unlockedDistricts.push(STORY_FOREST_DISTRICTS.sequenceStream);
  }
  const restoredMemoryTrees = [
    ...(Array.isArray(world.restoredMemoryTrees) ? world.restoredMemoryTrees : []),
  ];
  return {
    visited: world.visited === true,
    currentDistrict: asDistrict(world.currentDistrict),
    storyTreeDiscovered: world.storyTreeDiscovered === true,
    storyTreeStage,
    storyLanternsLit: [...(Array.isArray(world.storyLanternsLit) ? world.storyLanternsLit : [])],
    completedStoryTrails: [...(Array.isArray(world.completedStoryTrails) ? world.completedStoryTrails : [])],
    wordGardenDiscovered: world.wordGardenDiscovered === true,
    completedWordGardenChallenges: [
      ...(Array.isArray(world.completedWordGardenChallenges)
        ? world.completedWordGardenChallenges
        : []),
    ],
    wordGardenBlooms: [...(Array.isArray(world.wordGardenBlooms) ? world.wordGardenBlooms : [])],
    characterGroveDiscovered: world.characterGroveDiscovered === true,
    memoryTreeDiscovered: world.memoryTreeDiscovered === true,
    memoryTreeStage: restoredMemoryTrees.includes('memory-tree-restored-1')
      ? 'restored-1'
      : 'confused',
    characterGroveChallengeSnapshots: normalizeCharacterGroveSnapshots(
      world.characterGroveChallengeSnapshots,
    ),
    completedCharacterGroveChallenges: [
      ...(Array.isArray(world.completedCharacterGroveChallenges)
        ? world.completedCharacterGroveChallenges
        : []),
    ],
    restoredMemoryTrees,
    characterSkillMastery: normalizeCharacterSkillMastery(world.characterSkillMastery),
    unlockedDistricts,
    districtProgress: { ...(world.storyForestDistrictProgress ?? {}) },
    completedDistricts: normalizeDistricts(world.completedStoryForestDistricts),
    restorationEvents: [
      ...(Array.isArray(world.storyForestRestorationEvents)
        ? world.storyForestRestorationEvents
        : []),
    ],
  };
}

export function visitStoryForestDistrict(district: StoryForestDistrict) {
  const world = getRecord();
  const firstVisit = world.visited !== true;
  world.visited = true;
  world.currentDistrict = district;
  world.unlockedDistricts = addUnique(world.unlockedDistricts, district);
  SaveManager.setLastScene('StoryForest');
  SaveManager.save();
  return { firstVisit, state: getStoryForestState() };
}

export function markStoryTreeDiscovered() {
  const world = getRecord();
  world.storyTreeDiscovered = true;
  world.currentDistrict = STORY_FOREST_DISTRICTS.silentStoryTree;
  SaveManager.setLastScene('StoryForest');
  SaveManager.save();
  return getStoryForestState();
}

export function completeStoryTrail(
  challengeId: string,
  lanternId: string,
  unlockDistricts: readonly StoryForestDistrict[] = [],
) {
  const world = getRecord();
  const firstCompletion = !world.completedStoryTrails?.includes(challengeId);
  world.completedStoryTrails = addUnique(world.completedStoryTrails, challengeId);
  world.storyLanternsLit = addUnique(world.storyLanternsLit, lanternId);
  world.storyTreeStage = 'awakened-1';
  unlockDistricts.forEach(district => {
    world.unlockedDistricts = addUnique(world.unlockedDistricts, district);
  });
  SaveManager.setLastScene('StoryForest');
  SaveManager.save();
  return { firstCompletion, state: getStoryForestState() };
}

export function markWordGardenDiscovered() {
  const current = getStoryForestState();
  if (current.storyTreeStage !== 'awakened-1'
      || !current.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.wordGarden)) {
    return { newlyDiscovered: false, state: current };
  }

  const world = getRecord();
  const newlyDiscovered = world.wordGardenDiscovered !== true;
  world.wordGardenDiscovered = true;
  world.currentDistrict = STORY_FOREST_DISTRICTS.wordGarden;
  world.unlockedDistricts = addUnique(world.unlockedDistricts, STORY_FOREST_DISTRICTS.wordGarden);
  SaveManager.setLastScene('StoryForest');
  SaveManager.save();
  return { newlyDiscovered, state: getStoryForestState() };
}

export function completeWordGardenChallenge(challengeId: string, bloomEvent: string) {
  const world = getRecord();
  const firstCompletion = !world.completedWordGardenChallenges?.includes(challengeId);
  world.wordGardenDiscovered = true;
  world.completedWordGardenChallenges = addUnique(
    world.completedWordGardenChallenges,
    challengeId,
  );
  world.wordGardenBlooms = addUnique(world.wordGardenBlooms, bloomEvent);
  world.currentDistrict = STORY_FOREST_DISTRICTS.wordGarden;
  world.unlockedDistricts = addUnique(world.unlockedDistricts, STORY_FOREST_DISTRICTS.wordGarden);
  world.unlockedDistricts = addUnique(
    world.unlockedDistricts,
    STORY_FOREST_DISTRICTS.characterGrove,
  );
  SaveManager.setLastScene('StoryForest');
  SaveManager.save();
  return { firstCompletion, state: getStoryForestState() };
}

export function saveStoryForestDistrictProgress(
  district: StoryForestDistrict,
  snapshot: unknown,
) {
  const world = getRecord();
  world.storyForestDistrictProgress = {
    ...(world.storyForestDistrictProgress ?? {}),
    [district]: snapshot,
  };
  world.currentDistrict = district;
  world.unlockedDistricts = addUnique(world.unlockedDistricts, district);
  SaveManager.save();
  return getStoryForestState();
}

export function completeStoryForestDistrictProgress(
  district: StoryForestDistrict,
  snapshot: unknown,
  restorationEvent: string,
  nextDistrict?: StoryForestDistrict,
) {
  if (!restorationEvent.trim()) throw new RangeError('Story Forest restoration event must not be empty.');
  const world = getRecord();
  world.storyForestDistrictProgress = {
    ...(world.storyForestDistrictProgress ?? {}),
    [district]: snapshot,
  };
  world.completedStoryForestDistricts = addUnique(world.completedStoryForestDistricts, district);
  world.storyForestRestorationEvents = addUnique(
    world.storyForestRestorationEvents,
    restorationEvent,
  );
  world.currentDistrict = district;
  world.unlockedDistricts = addUnique(world.unlockedDistricts, district);
  if (nextDistrict) world.unlockedDistricts = addUnique(world.unlockedDistricts, nextDistrict);
  SaveManager.save();
  return getStoryForestState();
}

export function markCharacterGroveDiscovered() {
  const current = getStoryForestState();
  if (!current.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.characterGrove)) {
    return { newlyDiscovered: false, state: current };
  }
  const world = getRecord();
  const newlyDiscovered = world.characterGroveDiscovered !== true;
  world.characterGroveDiscovered = true;
  world.currentDistrict = STORY_FOREST_DISTRICTS.characterGrove;
  world.unlockedDistricts = addUnique(
    world.unlockedDistricts,
    STORY_FOREST_DISTRICTS.characterGrove,
  );
  SaveManager.setLastScene('StoryForest');
  SaveManager.save();
  return { newlyDiscovered, state: getStoryForestState() };
}

export function markMemoryTreeDiscovered() {
  const discovery = markCharacterGroveDiscovered();
  if (!discovery.state.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.characterGrove)) {
    return { newlyDiscovered: false, state: discovery.state };
  }
  const world = getRecord();
  const newlyDiscovered = world.memoryTreeDiscovered !== true;
  world.memoryTreeDiscovered = true;
  SaveManager.save();
  return { newlyDiscovered, state: getStoryForestState() };
}

export function saveCharacterGroveChallengeSnapshot(snapshot: CharacterGroveSnapshot) {
  if (!snapshot.challengeId.trim()) return getStoryForestState();
  const world = getRecord();
  world.characterGroveChallengeSnapshots = {
    ...(world.characterGroveChallengeSnapshots ?? {}),
    [snapshot.challengeId]: snapshot,
  };
  SaveManager.save();
  return getStoryForestState();
}

export function completeCharacterGroveChallenge(
  challengeId: string,
  restorationEvent: string,
  skillKind: string,
) {
  if (![challengeId, restorationEvent, skillKind].every(value => value.trim())) {
    throw new RangeError('Character Grove completion identifiers must not be empty.');
  }
  const current = getStoryForestState();
  if (!current.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.characterGrove)) {
    return { firstCompletion: false, state: current };
  }

  const world = getRecord();
  const firstCompletion = !world.completedCharacterGroveChallenges?.includes(challengeId);
  world.characterGroveDiscovered = true;
  world.memoryTreeDiscovered = true;
  world.completedCharacterGroveChallenges = addUnique(
    world.completedCharacterGroveChallenges,
    challengeId,
  );
  world.restoredMemoryTrees = addUnique(world.restoredMemoryTrees, restorationEvent);
  const characterSkillMastery = world.characterSkillMastery ?? {};
  characterSkillMastery[skillKind] = addUnique(characterSkillMastery[skillKind], challengeId);
  world.characterSkillMastery = characterSkillMastery;
  world.currentDistrict = STORY_FOREST_DISTRICTS.characterGrove;
  world.unlockedDistricts = addUnique(
    world.unlockedDistricts,
    STORY_FOREST_DISTRICTS.characterGrove,
  );
  world.unlockedDistricts = addUnique(
    world.unlockedDistricts,
    STORY_FOREST_DISTRICTS.sequenceStream,
  );
  SaveManager.setLastScene('StoryForest');
  SaveManager.save();
  return { firstCompletion, state: getStoryForestState() };
}
